// Activity (redesign 2026-09-27): every drop-off, pickup and absence for the family, newest day
// first, filterable by child; each row opens the signed record.
import { useState } from "react";
import { Banner, Button, EmptyState, Spinner, Timeline, TimelineItem } from "@vavr/ui";
import { api } from "../shared/api.ts";
import { useAsync } from "../shared/useAsync.ts";
import { eventLabel } from "../shared/format.ts";
import { navigate } from "../shared/router.tsx";
import { G, useGuardian } from "./context.tsx";
import { byDay, sentence, type GuardianEvent } from "./status.ts";
import { EVENT_ICON, EVENT_TONE } from "./Home.tsx";

export function Activity() {
  const { overview, version } = useGuardian();
  const sites = overview.sites.filter((s) => s.children.length);
  const [child, setChild] = useState<string | null>(null);
  const q = useAsync(async () => {
    const lists = await Promise.all(sites.map((s) => api<{ events: GuardianEvent[] }>("GET", `/api/v1/guardian/sites/${s.site.id}/events`).then((r) => r.events.map((e) => ({ e, s })))));
    return lists.flat().sort((a, b) => b.e.server_ts - a.e.server_ts);
  }, [sites.map((s) => s.site.id).join(","), version]);
  const kids = sites.flatMap((s) => s.children);
  if (!sites.length) return <EmptyState icon="list" title="No children linked yet" />;
  if (q.loading && !q.data) return <div className="pmu-center"><Spinner label="Loading activity" /></div>;
  if (q.error) return <Banner tone="danger" title={q.error.message} actions={<Button size="sm" onClick={q.reload}>Try again</Button>} />;
  const rows = (q.data ?? []).filter((x) => !child || x.e.child_id === child);
  // One time zone per family in practice; the first school's decides the day headings.
  const tz = sites[0]!.site.tz;
  const groups = byDay(rows.map((x) => x.e), tz);
  const siteOf = new Map(rows.map((x) => [x.e.id, x.s]));
  return (
    <div className="pmu-stack">
      {kids.length > 1 && (
        <div className="pmt-chips" role="group" aria-label="Child">
          <button type="button" className="pmt-chip" aria-pressed={child === null} onClick={() => setChild(null)}>Everyone</button>
          {kids.map((c) => <button key={c.id} type="button" className="pmt-chip" aria-pressed={child === c.id} onClick={() => setChild(c.id)}>{c.first}</button>)}
        </div>
      )}
      {!groups.length ? <EmptyState icon="list" title="Nothing recorded yet" description="Drop-offs and pickups show here." /> : groups.map((g) => (
        <section key={g.key} className="pmu-stack" aria-label={g.heading}>
          <h2 className="pmu-g-section">{g.heading}</h2>
          <div className="pmu-g-panel">
            <Timeline label={g.heading}>
              {g.events.map((e) => {
                const s = siteOf.get(e.id)!;
                const first = s.children.find((c) => c.id === e.child_id)?.first ?? "Your child";
                const href = `${G}/sites/${s.site.id}/events/${e.id}`;
                return (
                  <TimelineItem key={e.id} icon={EVENT_ICON[e.type] ?? "note"} tone={EVENT_TONE[e.type] ?? "neutral"} title={sentence(e, first, s.site.tz)}
                    href={href} onClick={(ev) => { ev.preventDefault(); navigate(href); }}>
                    {`${eventLabel(e.type, "Update")}${e.photo_sha256 ? " · signed and photographed" : ""}`}
                  </TimelineItem>
                );
              })}
            </Timeline>
          </div>
        </section>
      ))}
    </div>
  );
}
